export const PREFERENCES_STORAGE_KEY = "ytnk_preferences";
export const PREFERENCES_UPDATED_EVENT = "ytnk_preferences_updated";

export type UsagePlan = "freemium" | "premium";

export interface StoredPreferences {
  dateFormat: string;
  timeFormat: string;
  currency: string;
  language: string;
  usagePlan: UsagePlan;
}

export const DEFAULT_PREFERENCES: StoredPreferences = {
  dateFormat: "yyyy_mm_dd",
  timeFormat: "h24",
  currency: "usd",
  language: "en",
  usagePlan: "freemium",
};

const DATE_FORMAT_MIGRATION: Record<string, string> = {
  "YYYY-MM-DD": "yyyy_mm_dd",
  "DD-MM-YYYY": "dd_mm_yyyy",
  "MM-DD-YYYY": "mm_dd_yyyy",
  "YYYY-AA-GG": "yyyy_mm_dd",
  "GG-AA-YYYY": "dd_mm_yyyy",
  "AA-GG-YYYY": "mm_dd_yyyy",
};

const TIME_FORMAT_MIGRATION: Record<string, string> = {
  "24 Hours": "h24",
  "12 Hours": "h12",
  "24 Saat": "h24",
  "12 Saat": "h12",
};

const CURRENCY_MIGRATION: Record<string, string> = {
  "US Dollar (USD)": "usd",
  "Euro (EUR)": "eur",
  "Pound Sterling (GBP)": "gbp",
  "Turkish Lira (TRY)": "try",
  "Amerikan Doları (USD)": "usd",
  "İngiliz Sterlini (GBP)": "gbp",
  "Türk Lirası (TRY)": "try",
};

const LANGUAGE_MIGRATION: Record<string, string> = {
  English: "en",
  "Türkçe": "tr",
};

const USAGE_PLAN_MIGRATION: Record<string, UsagePlan> = {
  freemium: "freemium",
  premium: "premium",
  Freemium: "freemium",
  Premium: "premium",
};

const toNormalizedCode = (
  rawValue: unknown,
  validCodes: string[],
  migrationMap: Record<string, string>,
  fallback: string
): string => {
  if (typeof rawValue !== "string" || rawValue.trim() === "") {
    return fallback;
  }

  if (validCodes.includes(rawValue)) {
    return rawValue;
  }

  const migratedValue = migrationMap[rawValue];
  if (migratedValue && validCodes.includes(migratedValue)) {
    return migratedValue;
  }

  return fallback;
};

const parseStoredPreferences = (): Record<string, unknown> => {
  const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);

  if (!stored) {
    return {};
  }

  try {
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }

  return {};
};

export const loadStoredPreferences = (): StoredPreferences => {
  const loadedPrefs = parseStoredPreferences();

  const normalizedPreferences: StoredPreferences = {
    dateFormat: toNormalizedCode(
      loadedPrefs.dateFormat,
      ["yyyy_mm_dd", "dd_mm_yyyy", "mm_dd_yyyy"],
      DATE_FORMAT_MIGRATION,
      DEFAULT_PREFERENCES.dateFormat
    ),
    timeFormat: toNormalizedCode(
      loadedPrefs.timeFormat,
      ["h24", "h12"],
      TIME_FORMAT_MIGRATION,
      DEFAULT_PREFERENCES.timeFormat
    ),
    currency: toNormalizedCode(
      loadedPrefs.currency,
      ["usd", "eur", "gbp", "try"],
      CURRENCY_MIGRATION,
      DEFAULT_PREFERENCES.currency
    ),
    language: toNormalizedCode(
      loadedPrefs.language,
      ["en", "tr"],
      LANGUAGE_MIGRATION,
      DEFAULT_PREFERENCES.language
    ),
    usagePlan: toNormalizedCode(
      loadedPrefs.usagePlan,
      ["freemium", "premium"],
      USAGE_PLAN_MIGRATION,
      DEFAULT_PREFERENCES.usagePlan
    ) as UsagePlan,
  };

  if (JSON.stringify(loadedPrefs) !== JSON.stringify(normalizedPreferences)) {
    localStorage.setItem(
      PREFERENCES_STORAGE_KEY,
      JSON.stringify(normalizedPreferences)
    );
  }

  return normalizedPreferences;
};

export const saveStoredPreferences = (
  preferences: Partial<StoredPreferences>
): StoredPreferences => {
  const nextPreferences = {
    ...loadStoredPreferences(),
    ...preferences,
  };

  localStorage.setItem(
    PREFERENCES_STORAGE_KEY,
    JSON.stringify(nextPreferences)
  );

  window.dispatchEvent(
    new CustomEvent(PREFERENCES_UPDATED_EVENT, {
      detail: nextPreferences,
    })
  );

  return nextPreferences;
};

export const saveStoredPreference = <K extends keyof StoredPreferences>(
  key: K,
  value: StoredPreferences[K]
): StoredPreferences => saveStoredPreferences({ [key]: value });

export const getUsagePlan = (): UsagePlan => loadStoredPreferences().usagePlan;

export const isPremiumUsage = (): boolean => getUsagePlan() === "premium";
